import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import moment from 'moment';
import _ from 'lodash';

import {
	addToPartsCart,
	clearPartsSearchResult,
	saveAvailabilityToCart,
	saveOrderabilityToCart,
	savePartsSearchResult,
	savePricesToCart,
	saveToCart,
	updateCartOrderPoints
} from '../../../../redux/actions';
import useDebounce from '../../../../hooks/useDebounce';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../../common/messageConstants';
import { shippingMethods } from '../../../common/partsNetConstants';

const PartsNetItemSearchBox = forwardRef((props, ref) => {
	const [keyword, setKeyword] = useState('');
	const debouncedKeyword = useDebounce(keyword.replaceAll(/\s\s+/g, ' ').trim(), 500);
	const debouncedKeyWordRef = useRef(debouncedKeyword);
	debouncedKeyWordRef.current = debouncedKeyword;
	const [showSearchResult, setShowSearchResult] = useState(false);
	const {
		user: { userDetails, selectedAccount, selectedPriceSource },
		cart,
		product: { partsSearchResult }
	} = useSelector(({ user, cart, product }) => ({ user, cart, product }));
	const dispatch = useDispatch();
	const searchInputRef = useRef();
	useImperativeHandle(ref, () => () => {
		setKeyword('');
		dispatch(clearPartsSearchResult());
	});

	useEffect(() => {
		setKeyword('');
	}, [selectedAccount, selectedPriceSource]);

	useEffect(() => {
		if (debouncedKeyword && debouncedKeyword.length >= 3) {
			dispatch(
				savePartsSearchResult({
					keyword: debouncedKeyword,
					inProgress: true,
					items: []
				})
			);

			props.getItemDetails(
				{
					items: debouncedKeyword
						.split(/[^\d\w-]/g)
						.filter(keyword => keyword)
						.map(itemNumber => ({ itemNumber })),
					agreementNumber: selectedPriceSource.AGREEMENT_NUMBER
				},
				searchResult => {
					if (debouncedKeyword === debouncedKeyWordRef.current) {
						dispatch(
							savePartsSearchResult({
								inProgress: false,
								items: _.uniqBy(
									searchResult.items.filter(item => !item.isInvalid),
									'itemNumber'
								)
							})
						);
					}
				},
				{
					searchContext: 'PNET',
					include: {
						pim: true,
						availability: true,
						orderability: true,
						pricing: true,
						itemSearch: true
					},
					skipPOKCheck: true
				}
			);
		}
	}, [debouncedKeyword]);

	const handleChange = e => {
		setKeyword(e.target.value.toUpperCase());
	};

	const handleBlur = e => {
		setTimeout(() => setShowSearchResult(false), 100);
	};

	const handleSubmit = e => {
		e.preventDefault();
		searchInputRef.current.blur();

		browserHistory.push(`/search-result/product-number/${keyword}`);
	};

	const handleItemInfo = (item, e) => {
		e.preventDefault();
		!item.isNLA && item.itemData && item.itemData.imagePath && browserHistory.push(`/product-specs/product-number/${item.itemNumber}`);
	};

	const handleAddToCart = item => {
		let cartPayload = cart.cart && cart.cart.responseObject;

		if (!cartPayload || !cartPayload.cartItems || !cartPayload.cartItems.length) {
			cartPayload = {
				accountNumber: selectedAccount.ACCOUNTNUMBER,
				createdDate: moment().format('MM/DD/YYYY'),
				updatedDate: moment().format('MM/DD/YYYY'),
				userId: userDetails.sso,
				orderType: 'R',
				requestedShipDate: moment().format('MM/DD/YYYY'),
				shipMethod: shippingMethods[props.isInternationalPartsCustomer ? 'international' : 'domestic']['regular'][0].displayName,
				shipSet: '',
				cartItems: [
					{
						id: item.id,
						item: item.itemNumber,
						quantity: item.isPOKInstall ? item.pimData.geego_pok_ord_qty : 1,
						itemDescription: item.pimData && item.pimData.item_description,
						agreementNumber: `${item.agreementNumber || selectedPriceSource.AGREEMENT_NUMBER}`,
						createdDate: moment().format(DATE_TIME_FORMAT),
						updatedDate: moment().format(DATE_TIME_FORMAT),
						poNumber: '',
						parentItem: item.originalItemNumber,
						buyerLineNo: item.id,
						imagePath: (item.itemData && item.itemData.imagePath) || ''
					}
				]
			};
		} else {
			cartPayload = {
				...cartPayload,
				cartItems: [
					...cartPayload.cartItems,
					{
						id: item.id,
						item: item.itemNumber,
						quantity: item.isPOKInstall ? item.pimData.geego_pok_ord_qty : 1,
						itemDescription: item.pimData && item.pimData.item_description,
						agreementNumber: `${item.agreementNumber || selectedPriceSource.AGREEMENT_NUMBER}`,
						createdDate: moment().format(DATE_TIME_FORMAT),
						updatedDate: moment().format(DATE_TIME_FORMAT),
						poNumber: '',
						parentItem: item.originalItemNumber,
						buyerLineNo: item.id,
						imagePath: (item.itemData && item.itemData.imagePath) || ''
					}
				]
			};
		}

		dispatch(
			addToPartsCart(cartPayload, cartResponse => {
				if (cartResponse.status && cartResponse.responseObject) {
					dispatch(saveToCart(cartResponse));

					const cartPimData = JSON.parse(JSON.stringify(cart.cartPimData ? cart.cartPimData.pimData : [])) || [];
					const cartOrderabilityData = cart.orderability ? [...cart.orderability] : [];
					const cartAvailabilityData = cart.availability ? [...cart.availability] : [];
					const cartPricingData = [];

					if (item.pimData && !cartPimData.find(pimItem => pimItem.item_number === item.itemNumber)) {
						cartPimData.push(item.pimData);
					}

					if (item.orderabilityData && !cartOrderabilityData.some(orderabilityItem => orderabilityItem.INVENTORY_ITEM === item.itemNumber)) {
						cartOrderabilityData.push(item.orderabilityData);
					}

					if (item.availabilityData) {
						cartAvailabilityData.push(item.availabilityData);
					}

					if (item.pricingData) {
						if (!cartPricingData.find(pricingItem => pricingItem.item_number === item.itemNumber)) {
							cartPricingData.push(item.pricingData);
						}
					}

					dispatch(updateCartOrderPoints({ pimData: cartPimData }));
					dispatch(saveOrderabilityToCart({ responseObject: cartOrderabilityData }));
					dispatch(saveAvailabilityToCart({ responseObject: cartAvailabilityData }));
					if (cartPricingData.length) {
						dispatch(savePricesToCart({ responseObject: { items: cartPricingData } }, true));
					}
				} else {
					browserHistory.push('/error');
				}
			})
		);
	};

	const itemInCart = useMemo(() => {
		if (
			partsSearchResult &&
			partsSearchResult.items &&
			cart.cart &&
			cart.cart.responseObject &&
			cart.cart.responseObject.cartItems &&
			cart.cart.responseObject.cartItems.length
		) {
			return partsSearchResult.items.reduce((acc, cur) => {
				acc[cur.itemNumber] = cart.cart.responseObject.cartItems.some(cartItem => cartItem.item === cur.itemNumber);
				return acc;
			}, {});
		}
		return {};
	}, [partsSearchResult, cart]);

	return (
		<form className={props.isMobileView ? undefined : 'mastheadSearchForm'} onSubmit={handleSubmit}>
			{!props.isMobileView && <label htmlFor="searchFormSearchBox">Full item is required. For multiple items, enter a space between item numbers.</label>}
			<div className="flex">
				<input
					type="text"
					name="searchFormSearchBox"
					className={props.isMobileView ? 'searchFormSearchMobile' : 'searchFormSearchBox'}
					id="searchFormSearchBox"
					value={keyword}
					onChange={handleChange}
					onBlur={handleBlur}
					onFocus={setShowSearchResult.bind(this, true)}
					autoComplete="off"
					ref={searchInputRef}
					style={{ flexGrow: 1 }}
				/>
				&nbsp;
				<button
					className="blueButton inspira uppercase"
					disabled={!keyword || keyword.trim().length < 3}
					style={props.isMobileView ? { padding: '.5rem', fontSize: '1rem', borderRadius: '5px' } : undefined}
				>
					Search
				</button>
			</div>
			{(showSearchResult || props.isMobileView) && keyword.trim() && debouncedKeyword.trim() && partsSearchResult && (
				<div id="searchResults">
					<div className="searchResults">
						<h3 className={props.isMobileView ? 'searchResultsMobileHead' : undefined}>Search Results</h3>
						<ul className={props.isMobileView ? 'searchResultsUl' : 'searchProducts'}>
							{partsSearchResult.inProgress ? (
								<li>
									<br />
									<div className="loader">&nbsp;</div>
									<br />
								</li>
							) : partsSearchResult.items && partsSearchResult.items.length ? (
								partsSearchResult.items.map(item => {
									const { availabilityData: itemAvailabilityData, pricingData: itemPricingData } = item;

									const isQuantityNotAvailableOnRequestedDate = itemAvailabilityData && itemAvailabilityData.availableQuantity >= 1410065408;
									const itemData = itemAvailabilityData
										? {
												availableQuantity: itemAvailabilityData.availableQuantity,
												warehouseCode: itemAvailabilityData.sourceOrganizationCode,
												warehouseId: itemAvailabilityData.sourceOrganizationID,
												eta: itemAvailabilityData.arrivalDate,
												availabilityMessage:
													(isQuantityNotAvailableOnRequestedDate && 'BACKORDER') ||
													(itemAvailabilityData.availableQuantity && itemAvailabilityData.availableQuantity < item.requestedQuantity && 'PARTIAL') ||
													'',
												errorCode: itemAvailabilityData.errorCode
										  }
										: {};

									let unitPrice = 0;
									if (itemPricingData && itemPricingData.records && itemPricingData.records.length && itemPricingData.records[0].unitPrice) {
										unitPrice = itemPricingData.records[0].unitPrice;
									}

									const isError = !!(
										(!itemData.warehouseId && itemData.warehouseCode && !itemData.warehouseCode.includes(':')) ||
										(item.isRestricted && item.orderabilityData && item.orderabilityData.error)
									);
									const showItemDetails = !item.isRestricted && !item.isNLA && !!unitPrice && !isError;

									return (
										<li key={item.id} className="clearfix">
											<p className="prdtDesc">
												<a href="" onClick={handleItemInfo.bind(this, item)}>
													{item.itemNumber}
												</a>
												{props.isMobileView && ' '}
												{item.pimData && item.pimData.item_description}
												{item.originalItemNumber && (
													<span className="block my-0" style={{ color: '#7a7a7a' }}>
														Supersedes <strong style={{ color: '#1a63a2' }}>{item.originalItemNumber}</strong>
													</span>
												)}
											</p>
											{showItemDetails && (
												<div className="avail py-0">
													<b>Avail</b>
													{props.isMobileView && ' '}
													<React.Fragment>
														{isQuantityNotAvailableOnRequestedDate ? 0 : itemData.availableQuantity}
														{itemData.availabilityMessage && (
															<React.Fragment>
																<strong className="block" style={{ fontSize: '.5rem', color: '#7a7a7a', lineHeight: 1.5 }}>
																	{itemData.availabilityMessage}
																</strong>
																{isQuantityNotAvailableOnRequestedDate && itemData.eta && (
																	<strong
																		className="block"
																		style={{ width: 'max-content', fontSize: '.5rem', color: '#7a7a7a', lineHeight: 1.5 }}
																	>
																		ETA: {moment(itemData.eta).format(DATE_FORMAT)}
																	</strong>
																)}
															</React.Fragment>
														)}
													</React.Fragment>
												</div>
											)}
											{isError && <p className="avail small red my-0 py-0">An error has occured</p>}
											<div>
												<p className="addProductBtn">
													<button
														type="button"
														className={`blockButton ${item.isNLA || itemInCart[item.itemNumber] ? ' grayButton' : ' orangeButton'}`}
														onClick={handleAddToCart.bind(this, item)}
														disabled={item.isRestricted || item.isNLA || !unitPrice || !itemData.warehouseId || isError || itemInCart[item.itemNumber]}
														style={{ fontSize: 'inherit' }}
													>
														{item.isRestricted
															? `Restricted${item.restrictionReason ? ` - ${item.restrictionReason}` : ''}`
															: item.isNLA
															? 'NLA'
															: !unitPrice
															? 'Not in Price List'
															: itemInCart[item.itemNumber]
															? 'In Cart'
															: 'Add To Cart'}
													</button>
												</p>
											</div>
										</li>
									);
								})
							) : (
								<li>No results found</li>
							)}
						</ul>
					</div>
				</div>
			)}
		</form>
	);
});

export default PartsNetItemSearchBox;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/common/Header/partsNetItemSearchBox.jsx